// src/components/themes/default/admin/analytics/CustomerAnalyticsView.tsx
"use client";

import { useState, useMemo } from "react";
import {
  Users,
  Search,
  Download,
  ArrowUpRight,
  ShieldAlert,
  Activity,
  Award,
} from "lucide-react";

interface CustomerAnalyticsProps {
  data: any;
  isLight: boolean;
  timeRange: "7d" | "30d" | "90d";
  onRefresh?: () => void;
}

export default function CustomerAnalyticsView({
  data,
  isLight,
  timeRange,
}: CustomerAnalyticsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredScatterPoint, setHoveredScatterPoint] = useState<any | null>(null);
  const [activeFunnelStage, setActiveFunnelStage] = useState<number | null>(null);

  const totalCustomers = data?.total_customers || 0;
  const retention = data?.retention || { repeat_pct: 0, new_pct: 0, repeat_count: 0, new_count: 0 };
  const loginActivity = data?.login_activity || [];
  const topBuyers = data?.top_buyers_volume || [];
  const rfmSegments = data?.rfm_segments || [];
  const scatterPoints = data?.scatter_spend || [];
  const conversionFunnel = data?.conversion_funnel || [];

  const kpiStats = useMemo(() => {
    const repeatRatio = retention.repeat_pct || 0;
    const highRisk = rfmSegments.find((s: any) => s.tier?.toLowerCase().includes("risk"))?.count || 0;
    const avgAov = topBuyers.length
      ? Math.round(topBuyers.reduce((acc: number, b: any) => acc + (b.spent || 0), 0) / topBuyers.length)
      : 0;

    return {
      total: totalCustomers,
      repeatRatio,
      highRiskCount: highRisk,
      averageBuyerValue: avgAov,
    };
  }, [totalCustomers, retention, rfmSegments, topBuyers]);

  const filteredBuyers = useMemo(() => {
    return topBuyers.filter((b: any) => {
      return (
        b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.phone?.includes(searchTerm)
      );
    });
  }, [topBuyers, searchTerm]);

  const handleExportCSV = () => {
    if (!topBuyers.length) return;
    const headers = ["Customer Name", "Phone", "Products Purchased", "Total Spent (BDT)"];
    const rows = topBuyers.map((b: any) => [
      `"${b.name}"`,
      `"${b.phone}"`,
      b.items,
      b.spent,
    ]);
    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `customer-cohort-intelligence-${timeRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* KPI Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Total Customer Base</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-blue-50 text-blue-600" : "bg-blue-500/10 text-blue-400"}`}>
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight">{kpiStats.total.toLocaleString()}</div>
          <div className="flex items-center gap-1.5 pt-1 text-xs font-bold text-emerald-500">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>Verified in Database</span>
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Repeat Retention (LTV)</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-emerald-50 text-emerald-600" : "bg-emerald-500/10 text-emerald-400"}`}>
              <Activity className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-emerald-600 dark:text-emerald-400">
            {kpiStats.repeatRatio}%
          </div>
          <div className="text-xs text-slate-400 font-bold pt-1">
            {retention.repeat_count} repeat buyers out of {kpiStats.total}
          </div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Top Buyers Avg Spend</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-amber-50 text-amber-600" : "bg-amber-500/10 text-amber-400"}`}>
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight">
            ৳{kpiStats.averageBuyerValue.toLocaleString()}
          </div>
          <div className="text-xs text-amber-500 font-bold pt-1">Based on OrderItem Volume</div>
        </div>

        <div
          className={`p-5 rounded-3xl border-2 transition-all ${
            isLight
              ? "bg-white border-slate-300 text-slate-900 shadow-sm"
              : "bg-slate-900 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-400">Churn Risk Alert</span>
            <div className={`p-2 rounded-xl ${isLight ? "bg-rose-50 text-rose-600" : "bg-rose-500/10 text-rose-400"}`}>
              <ShieldAlert className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-rose-600 dark:text-rose-400">
            {kpiStats.highRiskCount} Accounts
          </div>
          <div className="text-xs text-rose-500 font-bold pt-1">Inactive &gt; 60 days</div>
        </div>
      </div>

      {/* Row 1: Funnel & Retention Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-5 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Full Customer Conversion Funnel
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                Visitors → Product Views → Cart → Checkout → Delivered
              </p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-500 border border-blue-500/20 font-mono">
              E-Commerce Dropoff
            </span>
          </div>

          <div className="space-y-3 pt-1">
            {conversionFunnel.map((step: any, idx: number) => {
              const isActive = activeFunnelStage === idx;
              return (
                <div
                  key={idx}
                  onMouseEnter={() => setActiveFunnelStage(idx)}
                  onMouseLeave={() => setActiveFunnelStage(null)}
                  className={`p-3 rounded-2xl border-2 transition-all cursor-pointer ${
                    isActive
                      ? isLight
                        ? "bg-slate-100 border-blue-500 shadow-md scale-[1.01]"
                        : "bg-slate-800 border-blue-400 shadow-lg scale-[1.01]"
                      : isLight
                      ? "bg-slate-50/70 border-slate-200"
                      : "bg-slate-950/60 border-slate-800/80"
                  }`}
                >
                  <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center text-[10px] font-mono font-black">
                        {idx + 1}
                      </span>
                      {step.stage}
                    </span>
                    <span className="font-mono text-sm font-black">
                      {step.count.toLocaleString()}{" "}
                      <span className="text-[10px] text-slate-400 font-normal">({step.pct})</span>
                    </span>
                  </div>

                  <div className={`w-full h-3 rounded-full overflow-hidden ${isLight ? "bg-slate-200" : "bg-slate-800"}`}>
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${step.color}`}
                      style={{ width: step.width }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-5 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Repeat Retention Cohort
              </h3>
              <p className="text-xs text-slate-500 font-medium">Customer loyalty & repeat purchase ratio</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-emerald-500">LTV Metric</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-around py-4 gap-6">
            <div className="relative w-40 h-40 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                <path
                  className="text-slate-200 dark:text-slate-800"
                  strokeWidth="3.8"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-emerald-500"
                  strokeDasharray={`${retention.repeat_pct}, 100`}
                  strokeWidth="4.8"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute text-center">
                <span className="text-3xl font-black font-mono text-emerald-600 dark:text-emerald-400">
                  {retention.repeat_pct}%
                </span>
                <span className="text-[10px] block text-slate-400 font-black uppercase tracking-wider">
                  Repeat LTV
                </span>
              </div>
            </div>

            <div className="space-y-3 w-full text-xs font-bold">
              <div
                className={`p-3.5 rounded-2xl border-2 flex items-center justify-between ${
                  isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 shrink-0" />
                  <span>Repeat Buyers</span>
                </div>
                <span className="font-mono text-emerald-600 dark:text-emerald-400 font-black">
                  {retention.repeat_count} Users
                </span>
              </div>

              <div
                className={`p-3.5 rounded-2xl border-2 flex items-center justify-between ${
                  isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-3.5 h-3.5 rounded-full bg-slate-300 dark:bg-slate-700 shrink-0" />
                  <span>Single-Order Buyers</span>
                </div>
                <span className="font-mono text-slate-500 font-black">{retention.new_count} Users</span>
              </div>
            </div>
          </div>

          <div
            className={`p-3 rounded-2xl border text-[11px] font-medium leading-relaxed ${
              isLight ? "bg-slate-50 border-slate-200 text-slate-600" : "bg-slate-950/60 border-slate-800 text-slate-400"
            }`}
          >
            💡 কাস্টমার রিটেনশন রেট ৫০% এর ওপরে থাকা স্বাস্থ্যকর আউটলেটের লক্ষণ। লয়্যাল ক্রেতাদের জন্য বিশেষ কুপন চালু করতে পারেন।
          </div>
        </div>
      </div>

      {/* Row 2: Scatter Plot & Recency Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className={`lg:col-span-7 p-6 rounded-3xl border-2 space-y-4 transition-all ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Customer Spend vs Order Frequency Scatter Plot
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                X-Axis: Order Frequency | Y-Axis: Total Spend
              </p>
            </div>
            <span className="text-[10px] font-mono font-bold text-amber-500">Scatter Plot</span>
          </div>

          <div className="h-64 relative w-full pt-3">
            <svg viewBox="0 0 450 180" className="w-full h-full overflow-visible">
              <line x1="30" y1="20" x2="30" y2="160" stroke={isLight ? "#cbd5e1" : "#334155"} strokeWidth="1.5" />
              <line x1="30" y1="160" x2="430" y2="160" stroke={isLight ? "#cbd5e1" : "#334155"} strokeWidth="1.5" />
              <line x1="30" y1="90" x2="430" y2="90" stroke={isLight ? "#f1f5f9" : "#1e293b"} strokeDasharray="3" />
              <line x1="30" y1="30" x2="430" y2="30" stroke={isLight ? "#f1f5f9" : "#1e293b"} strokeDasharray="3" />

              <text x="5" y="32" fill="#94a3b8" fontSize="8" fontFamily="monospace">Max</text>
              <text x="5" y="92" fill="#94a3b8" fontSize="8" fontFamily="monospace">Mid</text>
              <text x="15" y="162" fill="#94a3b8" fontSize="8" fontFamily="monospace">৳0</text>

              {scatterPoints.map((pt: any, idx: number) => {
                const isHovered = hoveredScatterPoint?.label === pt.label;
                return (
                  <g
                    key={idx}
                    onMouseEnter={() => setHoveredScatterPoint(pt)}
                    onMouseLeave={() => setHoveredScatterPoint(null)}
                    className="cursor-pointer"
                  >
                    <circle
                      cx={pt.x}
                      cy={pt.y}
                      r={isHovered ? pt.r + 3 : pt.r}
                      fill={pt.fill}
                      stroke={isLight ? "#ffffff" : "#0f172a"}
                      strokeWidth="2"
                      className="transition-all duration-200"
                    />
                    <text
                      x={pt.x - 20}
                      y={pt.y - 12}
                      fill={isLight ? "#0f172a" : "#ffffff"}
                      fontSize="8"
                      fontWeight="bold"
                    >
                      {pt.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            {hoveredScatterPoint && (
              <div
                className={`absolute top-2 right-4 p-2.5 rounded-xl border shadow-xl text-xs font-mono z-30 animate-in fade-in duration-150 ${
                  isLight ? "bg-white border-slate-300 text-slate-900" : "bg-slate-950 border-slate-700 text-white"
                }`}
              >
                <div className="font-bold">{hoveredScatterPoint.label}</div>
                <div className="text-[10px] text-emerald-500 font-bold">
                  Total Spent: ৳{hoveredScatterPoint.spent?.toLocaleString()} | Orders: {hoveredScatterPoint.orders}
                </div>
              </div>
            )}

            <div className="flex justify-between text-[10px] font-mono text-slate-400 px-6 pt-2">
              <span>1-2 Orders (Casual)</span>
              <span>3-5 Orders (Regular)</span>
              <span>6-10 Orders (Loyal)</span>
              <span>10+ Orders (Champions)</span>
            </div>
          </div>
        </div>

        <div
          className={`lg:col-span-5 p-6 rounded-3xl border-2 space-y-5 transition-all flex flex-col justify-between ${
            isLight
              ? "bg-white border-slate-300 shadow-sm text-slate-900"
              : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
          }`}
        >
          <div className="flex items-center justify-between border-b pb-3.5 border-slate-100 dark:border-slate-800/80">
            <div>
              <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
                Activity & Recency Timeline
              </h3>
              <p className="text-xs text-slate-500 font-medium">Customer activity status breakdown</p>
            </div>
            <span className="text-[10px] font-mono font-bold text-slate-400">Total: {totalCustomers}</span>
          </div>

          <div className="space-y-3.5 pt-1">
            {loginActivity.map((tier: any, idx: number) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between text-xs font-bold">
                  <span className={isLight ? "text-slate-800" : "text-slate-300"}>{tier.label}</span>
                  <span className="font-mono">
                    {tier.count} users ({tier.pct}%)
                  </span>
                </div>
                <div className={`w-full h-3 rounded-full overflow-hidden ${isLight ? "bg-slate-200" : "bg-slate-800"}`}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${tier.pct}%`, backgroundColor: tier.color }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            className={`p-3.5 rounded-2xl border flex items-center justify-between text-xs font-bold ${
              isLight ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-slate-950 border-slate-800 text-slate-300"
            }`}
          >
            <span>Dormant Churn Trigger</span>
            <span className="text-rose-500 font-mono font-black">60 Days Window</span>
          </div>
        </div>
      </div>

      {/* Row 3: Top Buyers Leaderboard */}
      <div
        className={`p-6 rounded-3xl border-2 space-y-5 transition-all ${
          isLight
            ? "bg-white border-slate-300 shadow-sm text-slate-900"
            : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4 border-slate-100 dark:border-slate-800/80">
          <div>
            <h3 className={`text-base font-black ${isLight ? "text-slate-950" : "text-white"}`}>
              Top High-Value Buyers & Product Volume
            </h3>
            <p className="text-xs text-slate-500 font-medium">
              Aggregated directly from customer transactions
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search buyer name/phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 pr-3 py-1.5 rounded-xl border text-xs font-bold outline-none transition-all ${
                  isLight
                    ? "bg-slate-50 border-slate-300 text-slate-900 focus:bg-white focus:border-blue-500"
                    : "bg-slate-950 border-slate-800 text-white focus:border-emerald-500"
                }`}
              />
            </div>

            <button
              onClick={handleExportCSV}
              className={`px-3 py-1.5 rounded-xl border-2 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                isLight
                  ? "bg-white hover:bg-slate-100 border-slate-300 text-slate-800"
                  : "bg-slate-800 hover:bg-slate-700 border-slate-700 text-white"
              }`}
              title="Download CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full text-left text-xs min-w-[650px] border-collapse">
            <thead
              className={`uppercase text-[10px] font-black border-y-2 ${
                isLight ? "bg-slate-100 text-slate-800 border-slate-300" : "bg-slate-950/60 text-slate-400 border-slate-800"
              }`}
            >
              <tr>
                <th className="py-3 px-4">Rank</th>
                <th className="py-3 px-4">Customer Info</th>
                <th className="py-3 px-4">Total Orders</th>
                <th className="py-3 px-4">Products Purchased</th>
                <th className="py-3 px-4">Total Spend (BDT)</th>
                <th className="py-3 px-4 text-right">Volume Share</th>
              </tr>
            </thead>
            <tbody className="divide-y font-medium">
              {filteredBuyers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400 text-xs">
                    কোনো বায়ারের ডেটা খুঁজে পাওয়া যায়নি।
                  </td>
                </tr>
              ) : (
                filteredBuyers.map((b: any, idx: number) => (
                  <tr
                    key={idx}
                    className={`border-b transition-colors ${
                      isLight
                        ? "border-slate-200 even:bg-slate-50/60 hover:bg-slate-100 text-slate-900"
                        : "border-slate-800/60 hover:bg-slate-800/30 text-slate-300"
                    }`}
                  >
                    <td className="py-3.5 px-4 font-mono font-black text-blue-600 dark:text-emerald-400">
                      #{idx + 1}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className={`font-black ${isLight ? "text-slate-950" : "text-white"}`}>{b.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono font-semibold">{b.phone}</div>
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold">{b.order_count || "—"} Orders</td>
                    <td className="py-3.5 px-4 font-mono font-black text-emerald-600 dark:text-emerald-400">
                      {b.items} Units
                    </td>
                    <td className="py-3.5 px-4 font-mono font-black">৳{Number(b.spent || 0).toLocaleString()}</td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="w-24 ml-auto h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"
                          style={{ width: b.width || "50%" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}